import { createSelector } from 'reselect';

import { GlobalState } from 'ha/types/store';
import {
  DateFilterValue,
  TypeFilterValue,
  SearchAreaFilterValue,
  SearchAreaValue,
} from 'ha/types/SearchFilters';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';
import { AvailabilityFilterValue, dateRangeToAvailability } from '@ha/date';
import { getFromStorage } from 'ha/utils/storage';

import { getUseMapAsAFilter, isInitialState, getBounds } from './mapSelectors';

export const getIsContractTypeFilterHidden = (state: GlobalState) =>
  state.search.contractTypeFilter.isHidden;

export const getIsFurnitureFilterHidden = (state: GlobalState) =>
  state.search.furnitureFilter.isHidden;

export const getIsGenderFilterHidden = (state: GlobalState) =>
  state.search.genderFilter.isHidden;

export const getIsRegistrationFilterHidden = (state: GlobalState) =>
  state.search.registrationFilter.isHidden;

export const getIsSuitableForFilterHidden = (state: GlobalState) =>
  state.search.suitableForFilter.isHidden;

export const getIsFacilitiesFilterHidden = (state: GlobalState) =>
  state.search.filters.facilities.isHidden;

export const getIsAmenitiesFilterHidden = (state: GlobalState) =>
  state.search.filters.amenities.isHidden;

export const getIsSearchAreaFilterHidden = createSelector(
  [isInitialState, getBounds, getFeatureFlags],
  (initialState, bounds, featureFlag) =>
    featureFlag.searchLocationFilters !== 'on' &&
    (featureFlag.searchUseMapAsAFilter !== 'on' ||
      !bounds ||
      (bounds && initialState)),
);
export const getDateFilter = (state: GlobalState) => state.search.dateFilter;
export const getAvailability = createSelector(
  getDateFilter,
  (dates): AvailabilityFilterValue | undefined => {
    if (!dates?.startDate) {
      return;
    }

    const { startDate, endDate } = dates;

    const availability = dateRangeToAvailability({ startDate, endDate });
    if (!availability) return;

    /*
    NOTE:
      It is not s good practice to use localStorage here, we need to refactor the whole selector to be in the flexible search module and here we should have it referenced.
      The initial values should be injected to the module and we should not use localStorage directly, in order to do so we need to add another layer for initialization so it
      effects the rest of the application, to avoid that we will keep it like the following so if we decided keeping the feature we will create the suitable architecture.
      otherwise it is easier to remove this way.
    */
    availability.moveIn.type =
      (getFromStorage('selectedDateType') as 'date' | 'month') ||
      availability.moveIn.type;

    return availability;
  },
);

export const getDateFilterValue = createSelector(
  getDateFilter,
  (dateFilter): DateFilterValue => ({
    startDate: dateFilter.startDate || null,
    endDate: dateFilter.endDate || null,
  }),
);

export const getCollection = (state: GlobalState) =>
  state.search.collectionFilter.collection;
export const getCollectionListingCount = (state: GlobalState) =>
  state.search.collectionFilter.listingCount;

export const getTypeFilterValue = (state: GlobalState): TypeFilterValue =>
  state.search.collectionFilter.categories;

export const getFurnitureFilter = (state: GlobalState) =>
  state.search.furnitureFilter.value;
export const getFurnitureListingCount = (state: GlobalState) =>
  state.search.furnitureFilter.listingCount;

export const getBillsFilter = (state: GlobalState) =>
  state.search.billsFilter.value;
export const getBillsListingCount = (state: GlobalState) =>
  state.search.billsFilter.listingCount;

const getRoomsFilterBedroomCount = (state: GlobalState) =>
  state.search.roomsFilter.bedroomCount;
export const getRoomsFilterValue = createSelector(
  getRoomsFilterBedroomCount,
  bedroomCount => ({
    bedroomCount,
  }),
);
export const getRoomsListingCount = (state: GlobalState) =>
  state.search.roomsFilter.listingCount;

export const getRulesFilter = (state: GlobalState) =>
  state.search.rulesFilter.value;

export const getFacilitiesFilter = (state: GlobalState) =>
  state.search.filters.facilities.value;
export const getFacilitiesListingCount = (state: GlobalState) =>
  state.search.filters.facilities.listingCount;

export const getAmenitiesFilter = (state: GlobalState) =>
  state.search.filters.amenities.value;
export const getAmenitiesListingCount = (state: GlobalState) =>
  state.search.filters.amenities.listingCount;

export const getPriceFilterValue = (state: GlobalState) =>
  state.search.priceFilter.value;

export const getCurrencyFilterValue = (state: GlobalState) =>
  state.search.priceFilter.currency;

export const getRegistrationFilter = (state: GlobalState) =>
  state.search.registrationFilter.value;
export const getRegistrationListingCount = (state: GlobalState) =>
  state.search.registrationFilter.listingCount;

export const getRecentlyAddedFilter = (state: GlobalState) =>
  state.search.recentlyAddedFilter;

export const getGenderFilter = (state: GlobalState) =>
  state.search.genderFilter.value;
export const getGenderListingCount = (state: GlobalState) =>
  state.search.genderFilter.listingCount;

export const getSuitableForFilter = (state: GlobalState) =>
  state.search.suitableForFilter.value;
export const getSuitableForListingCount = (state: GlobalState) =>
  state.search.suitableForFilter.listingCount;

export const getContractTypeFilter = (state: GlobalState) =>
  state.search.contractTypeFilter.value;
export const getContractTypeListingCount = (state: GlobalState) =>
  state.search.contractTypeFilter.listingCount;

export const getSearchAreaFilterValue = createSelector(
  getUseMapAsAFilter,
  (useMapAsAFilter): SearchAreaFilterValue => [
    useMapAsAFilter ? SearchAreaValue.MapBounds : SearchAreaValue.CityBounds,
  ],
);
export const getPropertySizeFilter = (state: GlobalState) =>
  state.search.filters.propertySize.value;
export const getPropertySizeListingCount = (state: GlobalState) =>
  state.search.filters.propertySize.listingCount;
export const getPropertySizeStats = (state: GlobalState) =>
  state.search.filters.propertySize.stats;

export const getRadiusFilter = (state: GlobalState) =>
  state.search.filters.radius.value;

export const getSortingFilter = (state: GlobalState) =>
  state.search.sorting.currentSorting;

// typings for createSelector only allow a limited number of selectors
const getFilterValuesTemp = createSelector(
  [
    getDateFilterValue,
    getTypeFilterValue,
    getFurnitureFilter,
    getBillsFilter,
    getRoomsFilterValue,
    getRulesFilter,
    getPriceFilterValue,
    getRegistrationFilter,
    getRecentlyAddedFilter,
    getGenderFilter,
    getSuitableForFilter,
  ],
  (
    dates,
    types,
    furniture,
    bills,
    rooms,
    rules,
    price,
    registration,
    recentlyAdded,
    gender,
    suitableFor,
    // eslint-disable-next-line max-params
  ) => ({
    dates,
    types,
    furniture,
    bills,
    rooms,
    rules,
    price,
    registration,
    recentlyAdded,
    gender,
    suitableFor,
  }),
);

export const getFilterValues = createSelector(
  [
    getFilterValuesTemp,
    getContractTypeFilter,
    getSearchAreaFilterValue,
    getPropertySizeFilter,
    getFacilitiesFilter,
    getAmenitiesFilter,
    getRadiusFilter,
    getCurrencyFilterValue,
  ],
  (
    filterValues,
    contractType,
    searchArea,
    propertySize,
    facilities,
    amenities,
    radius,
    currency,
    // eslint-disable-next-line max-params
  ) => ({
    ...filterValues,
    contractType,
    searchArea,
    propertySize,
    facilities,
    amenities,
    radius,
    currency,
  }),
);

export const getFiltersCount = createSelector(
  [
    getFurnitureListingCount,
    getSuitableForListingCount,
    getGenderListingCount,
    getRegistrationListingCount,
    getBillsListingCount,
    getCollectionListingCount,
    getContractTypeListingCount,
    getPropertySizeListingCount,
    getFacilitiesListingCount,
    getAmenitiesListingCount,
    getRoomsListingCount,
  ],
  (
    furniture,
    suitableFor,
    gender,
    registration,
    bills,
    propertyType,
    contractType,
    propertySize,
    facilities,
    amenities,
    rooms,
    // eslint-disable-next-line max-params
  ) => ({
    furniture,
    suitableFor,
    gender,
    registration,
    bills,
    propertyType,
    contractType,
    propertySize,
    facilities,
    amenities,
    rooms,
  }),
);

export const getNoFilterFacet = (state: GlobalState) =>
  state.search.filters.noFiltersFacet;
